import { FlightViewMapLibMap } from '../map'
import { InitProps, WLProps } from '../types'
import L from 'leaflet'
import './wl.css'
import { createWeatherContent } from '../atm/legend'
import { weatherZones, globalWeatherZone, conusZone, canadaZone } from '../weather'
import intersect from '@turf/intersect'
import { format } from 'date-fns'
import { Feature, MultiPolygon, Polygon } from '@turf/helpers'
import { FlightViewMapLibCities } from '../cities'

interface WeatherZone {
  name: string
  zone: Feature<Polygon | MultiPolygon | any>
  urlList?: string[]
  timeList?: number[]
}

const northAmerica = false
const initializationDelay = 5000
const refreshDelay = 1000 * 60 * 20
const minsGran = 2.5
const finePeriod = 135
const raaniPeriodPoz = 1000
const timeSub = new Date(2019, 9, 28).getTime()
const timeDiv = 10000

export class FlightViewMapLibWL {
  props: WLProps

  // submodule
  mapModule: FlightViewMapLibMap
  cities: FlightViewMapLibCities

  // map
  map: L.Map

  mapStyle: string
  ratio: number
  dateTime: Date
  startCount: number
  endCount: number
  count: number
  lastMins: number
  lastrat: number
  tohAnimate: any
  currentWeatherZones: WeatherZone[]
  lastIx: number
  currentWeather: WeatherZone
  weatherOverlays: { [key: string]: L.ImageOverlay } = {}
  lastUpdate: number = null
  currentOverlay: { [key: string]: L.ImageOverlay } = {}
  legendPlacement: string

  constructor (parentElement: HTMLElement, initProps: InitProps) {
    this.props = initProps.wl
    this.mapModule = new FlightViewMapLibMap(parentElement, initProps)
    this.map = this.mapModule.map
    this.mapStyle = initProps.mapStyle
    this.cities = new FlightViewMapLibCities(this.map, this.props)
    this.startCount = this.props.mins / minsGran
    this.endCount = 0 - raaniPeriodPoz / finePeriod
    this.currentWeatherZones = []
    this.legendPlacement = this.props.legendPlacement || 'bottomleft'

    void this.cities.initialize().then(async () => {
      const legend = L.Control.extend({
        options: {
          position: this.legendPlacement
        },
        onAdd: () => {
          const content = L.DomUtil.create('div', 'FVLib-wl-legend-content')
          content.appendChild(createWeatherContent())
          return content
        }
      })

      const instance = new legend()
      this.map.addControl(instance)
      this.onMapResize()
      if (this.props.mins > 1) {
        const progress = L.Control.extend({
          options: {
            position: 'topright'
          },
          onAdd: () => {
            const content = L.DomUtil.create('div', 'FVLib-wl-progressbar-content')
            const progress = L.DomUtil.create('div', 'FVLib-wl-progressbar')
            progress.id = 'FVLib-wl-progressbar'
            for (let i = 0; i < 4; ++i) {
              const hourmark = L.DomUtil.create('div', 'FVLib-wl-hourmark')
              hourmark.id = `FVLib-wl-hourmark-${i}`
              const hourlabl = L.DomUtil.create('div', 'FVLib-wl-hourlabl')
              hourlabl.id = `FVLib-wl-hourlabl-${i}`
              progress.appendChild(hourmark)
              progress.appendChild(hourlabl)
            }
            content.appendChild(progress)
            return content
          }
        })
        this.map.addControl(new progress())
        this.updateWeather().then(() => {
          this.mapModule.ready(true)
          this.scheduleWeatherUpdate()
          this.startAnimation()
        })
      }
    })
  }

  onMapResize () {
    if (this.props.bounds && this.props.bounds.length === 2) {
      const bounds = new L.LatLngBounds({
        lat: this.props.bounds[0][0],
        lng: this.props.bounds[0][1]
      }, { lat: this.props.bounds[1][0], lng: this.props.bounds[1][1] })
      L.bounds(this.props.bounds)
      this.map.fitBounds(bounds, { animate: false, padding: [20, 20] })
    } else if (this.cities) {
      const bounds = this.cities.getBounds()
      this.map.fitBounds(bounds, { animate: false, padding: [20, 20] })
    }
  }

  async sleep (ms: number) {
    return await new Promise(resolve => setTimeout(resolve, ms))
  }

  scheduleWeatherUpdate () {
    this.sleep(refreshDelay).then(() => {
      this.lastUpdate = null
      Object.values(this.weatherOverlays).forEach((overlay) => {
        overlay.remove()
      })
      this.weatherOverlays = {}
      this.currentWeatherZones = []
      this.updateWeather().then(() => {
        this.scheduleWeatherUpdate()
      })
    })
  }

  async updateWeather () {
    try {
      if (this.lastUpdate === null) {
        await this.retrieveAllWeatherImages()
        this.lastUpdate = Date.now()
      }
      this.weatherOverlays[globalWeatherZone.name]?.remove()
    } catch (err) {
      console.warn('Cannot update weather overlay', err)
    }
  }

  async retrieveAllWeatherImages () {
    if (northAmerica) {
      this.currentWeatherZones = [conusZone, canadaZone]
    } else {
      const bounds = L.rectangle(this.cities.getBounds()).toGeoJSON()
      for (const weather of weatherZones) {
        if (weather.name === globalWeatherZone.name) continue
        if (intersect(bounds, weather.zone)) {
          const weather1: WeatherZone = {
            name: weather.name,
            zone: weather.zone,
            urlList: [],
            timeList: []
          }
          this.currentWeatherZones.push(weather1)
        }
      }
    }

    const wxids = this.currentWeatherZones.map((zone) => zone.name).join(',')
    const response = await fetch(`https://tracker.flightview.com/FVAccess3/tools/dhtml/proxy/latestradar.aspx?c=${Date.now()}&wxid=${wxids}&mins=${this.props.mins}`)
    const text = await response.text()
    const lines = text.split('\n')
    for (const line of lines) {
      const split = line.split(':')
      if (split[2]) {
        const imageArray = split[2].split(',')
        const array = []
        const timeArray = []
        for (const img of imageArray) {
          const dt = this.url2Date(img)
          if (dt != null) { // successful parse to Date
            this.timeToLocal(dt, dt) // localize
            timeArray.push(this.date2Int(dt))
          }
          array.push(`https://tracker.flightview.com${split[1]}${img}`)
        }
        for (const weatherZone of this.currentWeatherZones) {
          if (weatherZone.name === split[0]) {
            weatherZone.urlList = array
            weatherZone.timeList = timeArray
          }
        }
      }
    }
    for (const weatherZone of this.currentWeatherZones) {
      void this.downloadWeatherImagesForZone(weatherZone)
    }
    // wait a bit so that we download the first images
    await this.sleep(initializationDelay)
  }

  async downloadWeatherImagesForZone (weatherZone: WeatherZone) {
    for (let i = weatherZone.urlList.length - 1; i >= 0; i--) {
      const overlay = this.createWeatherOverlay(new L.GeoJSON(weatherZone.zone).getBounds(), weatherZone.urlList[i])
      overlay.setOpacity(0)
      overlay.addTo(this.map)
      this.weatherOverlays[`${weatherZone.name}${i}`] = overlay
      // don't DDOS the server
      await this.sleep(300)
    }
  }

  async updateWeatherOverlay (url: string, ix: number) {
    let overlay = this.weatherOverlays[`${this.currentWeather.name}${ix}`]
    if (!overlay) {
      overlay = this.createWeatherOverlay(new L.GeoJSON(this.currentWeather.zone).getBounds(), url)
      overlay.setOpacity(0)
      overlay.addTo(this.map)
      if (overlay) {
        this.weatherOverlays[`${this.currentWeather.name}${ix}`] = overlay
      }
    }
    this.currentOverlay[this.currentWeather.name]?.setOpacity(0)
    overlay.setOpacity(1)
    this.currentOverlay[this.currentWeather.name] = overlay
  }

  createWeatherOverlay (bounds: L.LatLngBounds, url: string): L.ImageOverlay {
    return url ? L.imageOverlay(url, bounds, { opacity: 0.5, className: 'FVLib-wl-image-overlay' }) : null
  }

  /**
   * Progress Bar
   */
  initCount () {
    this.count = this.startCount
    this.setNow()
  }

  setNow () {
    const dattim = new Date()
    const minsnow = dattim.getUTCMinutes()
    if (this.lastMins === minsnow) return
    this.lastMins = minsnow
    for (let i = 0; i < 4; ++i) {
      this.setHourMark(0 - i)
    }
  }

  startAnimation () {
    this.initCount()
    if (this.tohAnimate) clearInterval(this.tohAnimate)
    this.tohAnimate = setInterval(() => {
      this.next()
    }, finePeriod)
  }

  showProgress (rat: number) {
    if (rat > 1.0) rat = 1.0
    if (this.lastrat === rat) return
    this.lastrat = rat
    const progressbar = document.getElementById('FVLib-wl-progressbar')
    progressbar.style.backgroundSize = `${800 * rat}px 14px`
  }

  async next () {
    if (--this.count >= 0) {
      const idtShowing = this.date2Int(new Date(new Date().getTime() - this.count * minsGran * 60000))
      for (let i = 0; i < this.currentWeatherZones.length; i++) {
        this.currentWeather = this.currentWeatherZones[i]
        await this.showRadarNearest(idtShowing)
        this.lastIx = -1
      }
      this.showProgress(1.0 - this.count / this.startCount)
    }
    if (this.count < this.endCount) {
      this.initCount()
    }
  }

  setHourMark (index: number) {
    const hourmark = document.getElementById(`FVLib-wl-hourmark-${Math.abs(index)}`)
    const hourlabl = document.getElementById(`FVLib-wl-hourlabl-${Math.abs(index)}`)
    this.setHour(index)
    const time = format(new Date(this.dateTime), 'ha')
    const t = Math.round(800 * this.ratio)
    hourmark.style.left = `${t - 0.5}px`
    hourlabl.style.left = `${t - 800 / 10 / 2}px`
    hourlabl.innerText = time
  }

  setHour (time: number) {
    this.dateTime = new Date()
    this.dateTime.setTime(this.dateTime.getTime() + time * 60 * 60 * 1000)
    this.dateTime.setMinutes(0)
    this.ratio = 1 - ((new Date()).getTime() - this.dateTime.getTime()) / (4 * 60 * 60 * 1000)
  }

  /**
   * Update the Weather Image
   *
   */
  async showRadarNearest (idtShowing: number) {
    let ibest = -1
    let sqbest = Number.MAX_VALUE
    for (let i = 0; i < this.currentWeather.urlList.length; i++) {
      let sq = idtShowing - this.currentWeather.timeList[i]
      sq *= sq
      if (sqbest > sq) {
        sqbest = sq
        ibest = i
      }
    }
    if (ibest < 0) {
      return false
    }
    await this.showRadarAtIf(ibest)
  }

  async showRadarAtIf (ibest: number) {
    if (this.lastIx === ibest) return false
    await this.showRadarAt(ibest)
  }

  async showRadarAt (ix: number) {
    const name = this.currentWeather.urlList[this.lastIx = ix]
    if (!name) return
    await this.updateWeatherOverlay(name, ix)
  }

  timeToLocal (din: Date, dout: Date) {
    dout.setTime(din.getTime() - 60000 * (new Date()).getTimezoneOffset())
  }

  date2Int (dt: Date) {
    return Math.floor((dt.getTime() - timeSub) / timeDiv)
  }

  url2Date (url: string) {
    const aurl = url.split('.')
    let iurl = aurl.length
    if (iurl < 3 || aurl[--iurl] !== 'png') return null
    const stamp = aurl[--iurl]
    if (stamp.length >= 12 && stamp[0] === '2') {
      try {
        const yyyy = parseInt(stamp.substring(0, 4))
        const MM = parseInt(stamp.substring(4, 6))
        const dd = parseInt(stamp.substring(6, 8))
        const HH = parseInt(stamp.substring(8, 10))
        const mm = parseInt(stamp.substring(10, 12))
        return new Date(yyyy, MM - 1, dd, HH, mm)
      } catch (e) {
        console.log('stamp parse threw %o', e)
      }
    }
    return null
  }
}
